/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Agent view insight panels, in the Prompt-view look (ui-panel surfaces).
 *
 *   - AgentDependenciesPanel  — the full dependency graph: what the agent needs
 *     (model, prompt, RAG, conversation prompts, judge) and what uses it (eval
 *     suites, conversations). Agents reference the most, so both live here.
 *   - AgentPerformancePanel   — response time, feedback sentiment and eval
 *     quality over the last 30 days (from /agents/{id}/activity).
 *   - AgentConversationsPanel — the recent conversations the agent answered.
 *   - AgentObservabilityPanel — a health summary over the agent's traces, each
 *     openable in full inline, with a deep-link to AI Observability.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Tag } from 'primereact/tag';
import { Dialog } from 'primereact/dialog';
import { formatDate } from 'sharedUi/dateUtils';

import { agentsService, type AgentDependentGroup, type AgentOverview } from '../../services/agents.service';
import { aiObservabilityService } from '../../services/ai-observability.service';
import { fmtCost, fmtMs, type ObsTraceRow } from '../../types/ai-observability.types';
import ObsTracingDetail from '../../pages/ai-observability/obs-tracing-detail';

const encodeId = (id: string): string => {
  try {
    return btoa(id).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch {
    return id;
  }
};

const LINK_BASE: Record<string, string> = { eval_suite: '/evals' };
const DEP_CATEGORIES: { type: AgentDependentGroup['type']; label: string }[] = [
  { type: 'eval_suite', label: 'Eval suites' },
  { type: 'conversation', label: 'Conversations' },
];

/** A subtle, truncating reference pill — a link when base + id are known. */
function RefChip({ base, id, label, tone = 'neutral' }: Readonly<{
  base?: string; id?: string | null; label: string; tone?: 'accent' | 'neutral';
}>) {
  const toneCls = tone === 'accent'
    ? 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900/50 dark:bg-indigo-900/20 dark:text-indigo-300'
    : 'border-gray-200 bg-gray-50 text-ink dark:border-gray-700 dark:bg-gray-800/60';
  const cls = `inline-flex max-w-[260px] items-center truncate rounded-md border px-2 py-0.5 text-xs transition-colors ${toneCls}`;
  if (base && id) {
    return <Link to={`${base}/${encodeId(id)}`} title={label}
      className={`${cls} hover:border-blue-400 hover:text-blue-600`}>{label}</Link>;
  }
  return <span title={label} className={cls}>{label}</span>;
}

export function AgentDependenciesPanel({
  agentId, overview,
}: Readonly<{ agentId: string; overview?: AgentOverview }>) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['agent-dependents', agentId],
    queryFn: () => agentsService.getDependents(agentId),
    enabled: !!agentId,
    staleTime: 30 * 1000,
  });
  const byType = new Map((data?.groups ?? []).map((g) => [g.type, g]));

  return (
    <div className="ui-panel p-5" data-testid="AgentView.Dependencies">
      <h2 className="text-sm font-semibold text-ink mb-0.5">Dependencies</h2>
      <p className="text-xs text-ink-muted mb-3">
        What this agent relies on, and everything that uses it. Click any name to open its record.
      </p>

      {/* Depends on — the agent's upstream refs, resolved to names. */}
      <div className="mb-3">
        <span className="text-xs text-ink-muted block mb-1">Depends on</span>
        <div className="flex flex-wrap gap-1.5">
          <RefChip tone="accent" base="/ai-models" id={overview?.model?.ref}
            label={`Model: ${overview?.model?.name || "The run's model"}`} />
          {overview?.prompt?.ref && (
            <RefChip tone="accent" base="/prompt-table" id={overview.prompt.ref}
              label={`Prompt: ${overview.prompt.name || overview.prompt.ref}${overview.prompt.version ? ` · v${overview.prompt.version}` : ''}`} />
          )}
          {overview?.rag?.ragCode && (
            <RefChip tone="accent" base="/rags" id={overview.rag.ragId}
              label={`RAG: ${overview.rag.name || overview.rag.ragCode}`} />
          )}
          {(overview?.conversationPrompts ?? []).map((cp) => (
            <RefChip key={cp.role} tone="accent" base="/prompt-table" id={cp.promptRef}
              label={`${cp.role}: ${cp.promptName || cp.promptRef}`} />
          ))}
          {overview?.onlineEval?.judgeModelRef && (
            <RefChip tone="accent" id={null}
              label={`Judge: ${overview.onlineEval.judgeModelName || overview.onlineEval.judgeModelRef}`} />
          )}
        </div>
      </div>

      {/* Used by — what embeds or targets this agent. */}
      <div>
        <span className="text-xs text-ink-muted block mb-1">
          Used by{data ? ` — ${data.total} reference${data.total === 1 ? '' : 's'}` : ''}
        </span>
        {isLoading && <p className="text-sm text-ink-muted">Loading dependents…</p>}
        {isError && <p className="text-sm text-ink-muted">Could not load dependents.</p>}
        {!isLoading && !isError && (
          <div className="flex flex-col gap-2">
            {DEP_CATEGORIES.map((cat) => {
              const g = byType.get(cat.type);
              const count = g?.count ?? 0;
              return (
                <div key={cat.type}>
                  <div className="text-xs font-medium text-ink mb-1">{cat.label} · {count}</div>
                  {count > 0 && g ? (
                    <div className="flex flex-wrap gap-1.5">
                      {g.items.map((it, i) => (
                        <RefChip key={it.id ?? i} base={LINK_BASE[g.type]} id={it.id} label={it.name} />
                      ))}
                      {g.count > g.items.length && (
                        <span className="text-xs text-ink-muted self-center">+{g.count - g.items.length} more</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-ink-muted">None</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const useActivity = (agentId: string) => useQuery({
  queryKey: ['agent-activity', agentId],
  queryFn: () => agentsService.getActivity(agentId, 30),
  enabled: !!agentId,
  staleTime: 30 * 1000,
});

export function AgentPerformancePanel({ agentId }: Readonly<{ agentId: string }>) {
  const { data, isLoading, isError } = useActivity(agentId);
  const rt = data?.responseTime;
  const fb = data?.feedback;
  const q = data?.quality;

  const kpis = [
    { label: 'Turns', val: rt ? rt.turns.toLocaleString() : '—' },
    { label: 'Avg response', val: fmtMs(rt?.avgLatencyMs ?? null) },
    { label: 'p95 response', val: fmtMs(rt?.p95LatencyMs ?? null) },
    { label: 'Time to first token', val: fmtMs(rt?.avgTtftMs ?? null) },
    { label: 'Feedback', val: fb && fb.total > 0 ? `👍 ${fb.positive} · 👎 ${fb.negative}` : '—' },
    { label: 'Avg rating', val: fb?.avgRatingNormalized != null ? `${Math.round(fb.avgRatingNormalized * 100)}%` : '—' },
    { label: 'Eval score', val: q?.avgEvalScore != null ? q.avgEvalScore.toFixed(2) : '—' },
    { label: 'Scored turns', val: q ? q.scoredTurns.toLocaleString() : '—' },
  ];

  return (
    <div className="ui-panel p-5" data-testid="AgentView.Performance">
      <h2 className="text-sm font-semibold text-ink mb-0.5">Performance</h2>
      <p className="text-xs text-ink-muted mb-3">
        How the agent has answered over the last 30 days — speed, the feedback people left, and eval quality.
      </p>
      {isError && <p className="text-sm text-ink-muted">Could not load performance.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {kpis.map((k) => (
          <div key={k.label} className="rounded bg-gray-50 dark:bg-gray-800 p-2">
            <div className="text-[11px] text-ink-muted">{k.label}</div>
            <div className="text-sm font-semibold text-ink tabular-nums">{isLoading ? '—' : k.val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AgentConversationsPanel({ agentId }: Readonly<{ agentId: string }>) {
  const { data, isLoading, isError } = useActivity(agentId);
  const recent = data?.conversations?.recent ?? [];

  return (
    <div className="ui-panel p-5" data-testid="AgentView.Conversations">
      <div className="flex items-start justify-between mb-2 gap-2">
        <div>
          <h2 className="text-sm font-semibold text-ink">Conversations</h2>
          <p className="text-xs text-ink-muted">The chat sessions this agent is answering, most recent first.</p>
        </div>
        {data?.conversations && data.conversations.total > 0 && (
          <span className="text-xs text-ink-muted whitespace-nowrap">{data.conversations.total} total</span>
        )}
      </div>
      {isLoading && <p className="text-sm text-ink-muted">Loading conversations…</p>}
      {isError && <p className="text-sm text-ink-muted">Could not load conversations.</p>}
      {!isLoading && !isError && recent.length === 0 && (
        <p className="text-sm text-ink-muted">No conversations run on this agent yet.</p>
      )}
      {recent.length > 0 && (
        <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700">
          {recent.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-sm text-ink" title={c.title}>{c.title}</div>
                <div className="text-xs text-ink-muted whitespace-nowrap">
                  {c.lastMessageAt ? `Last active ${formatDate(c.lastMessageAt)}` : (c.createdAt ? formatDate(c.createdAt) : '—')}
                </div>
              </div>
              <span className="text-xs text-ink-muted whitespace-nowrap flex-shrink-0">
                {c.messageCount != null ? `${c.messageCount} msgs` : '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const OBS_RANGES = [
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
];

const percentile = (xs: number[], p: number): number | null => {
  if (!xs.length) return null;
  const sorted = [...xs].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length))];
};

export function AgentObservabilityPanel({ agentCode }: Readonly<{ agentCode: string }>) {
  const [range, setRange] = useState('7d');
  const [openTrace, setOpenTrace] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['agent-observability', agentCode, range],
    queryFn: () => aiObservabilityService.getTracing({ agentCode, timeRange: range, limit: 50 }),
    enabled: !!agentCode,
    staleTime: 30 * 1000,
  });

  const rows: ObsTraceRow[] = data?.traces ?? [];
  const summary = data?.summary;

  const kpis = useMemo(() => {
    const calls = summary?.calls ?? 0;
    const failed = summary?.failed ?? 0;
    const latencies = rows.map((r) => r.latencyMs).filter((v): v is number => v != null);
    const costs = rows.map((r) => r.cost).filter((v): v is number => v != null);
    const currencies = new Set(rows.map((r) => r.currency).filter(Boolean));
    const totalCost = costs.length ? costs.reduce((a, b) => a + b, 0) : null;
    const avg = latencies.length ? latencies.reduce((a, b) => a + b, 0) / latencies.length : null;
    return {
      calls,
      successRate: calls > 0 ? `${Math.round(((calls - failed) / calls) * 100)}%` : '—',
      avgLatency: fmtMs(avg),
      p95Latency: fmtMs(percentile(latencies, 95)),
      totalCost: fmtCost(totalCost, currencies.size === 1 ? [...currencies][0] : null),
    };
  }, [summary, rows]);

  const deepLink = `/ai-observability/tracing?agentCode=${encodeURIComponent(agentCode)}`;

  return (
    <div className="ui-panel p-5" data-testid="AgentView.Observability">
      <div className="flex items-start justify-between mb-2 gap-2">
        <div>
          <h2 className="text-sm font-semibold text-ink">Observability</h2>
          <p className="text-xs text-ink-muted">
            Health of this agent's traced calls. Open any trace for its full step-by-step waterfall.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            className="text-xs border border-gray-200 dark:border-gray-700 rounded px-1.5 py-1 bg-transparent text-ink"
            value={range} onChange={(e) => setRange(e.target.value)} aria-label="Time range"
          >
            {OBS_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <Link to={deepLink} className="text-xs text-blue-600 hover:underline whitespace-nowrap">
            Open in AI Observability
          </Link>
        </div>
      </div>

      {isError && <p className="text-sm text-ink-muted">Could not load observability.</p>}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        {[
          { label: 'Calls', val: isLoading ? '—' : kpis.calls.toLocaleString() },
          { label: 'Success rate', val: isLoading ? '—' : kpis.successRate },
          { label: 'Avg latency', val: isLoading ? '—' : kpis.avgLatency },
          { label: 'p95 latency', val: isLoading ? '—' : kpis.p95Latency },
          { label: 'Total cost', val: isLoading ? '—' : kpis.totalCost },
        ].map((k) => (
          <div key={k.label} className="rounded bg-gray-50 dark:bg-gray-800 p-2">
            <div className="text-[11px] text-ink-muted">{k.label}</div>
            <div className="text-sm font-semibold text-ink tabular-nums">{k.val}</div>
          </div>
        ))}
      </div>

      {!isLoading && !isError && rows.length === 0 && (
        <p className="text-sm text-ink-muted">No traced calls in this window.</p>
      )}
      {rows.length > 0 && (
        <div className="flex flex-col divide-y divide-gray-100 dark:divide-gray-700">
          {rows.slice(0, 8).map((r) => {
            const failed = r.status === 'FAILED';
            return (
              <div key={r.observabilityId} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm text-ink truncate">{r.question || r.name || 'Agent call'}</div>
                  <div className="text-xs text-ink-muted whitespace-nowrap">{formatDate(r.startedAt)}</div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 text-xs text-ink-muted">
                  <span className="whitespace-nowrap">{fmtMs(r.latencyMs)}</span>
                  <span className="whitespace-nowrap">{fmtCost(r.cost, r.currency)}</span>
                  <Tag value={failed ? 'failed' : (r.status || 'ok')}
                    severity={failed ? 'danger' : 'success'} className="text-xs px-1.5 py-0.5" />
                  <button type="button" className="text-xs text-blue-600 hover:underline"
                    onClick={() => setOpenTrace(r.observabilityId)}>
                    View trace
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog
        header="Trace detail"
        visible={!!openTrace}
        onHide={() => setOpenTrace(null)}
        style={{ width: '64rem', maxWidth: '95vw' }}
        maximizable
        dismissableMask
      >
        <div className="max-h-[75vh] overflow-auto">
          {openTrace && <ObsTracingDetail observabilityId={openTrace} embedded />}
        </div>
      </Dialog>
    </div>
  );
}
